import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { translate } from 'lit-translate';
import { appState } from '../app.state';
import { actionRegistry } from '../actions/action-registry';
import { ActionIds } from '../actions/action.types';
import type { TimelineToolbarItem } from '../types/timeline-toolbar-item.interface';

/**
 * Resource filters toolbar item for the Resource-Management view.
 *
 * Renders two buttons:
 * - Reset button: clears all active resource filters (dispatches resourceFilters.clear),
 *   applied-count reads the isolated `numberOfAppliedResourceFilters` slot.
 * - Filter button: toggles the filter sidebar. Deliberately dispatches the shared
 *   `SIDEBAR_TOGGLE_TODO` (the `appState.todoFilterOpen` drawer): per PRD-100 the
 *   Resource view has no ToDo drawer to conflict with, so it reuses the ToDo drawer
 *   slot rather than introducing a `SIDEBAR_TOGGLE_RESOURCE` action. Only the filter
 *   DATA slot (`resourceFilters`) is isolated; the drawer open/collapse state is shared.
 *
 * Includes a login guard: renders nothing when the user is not logged in.
 */
export const resourceFiltersToolbarItem: TimelineToolbarItem = {
    id: 'resource-filters',
    position: 'start',
    order: -10,
    render: () => {
        if (!appState.isUserLoggedIn) {
            return nothing;
        }

        const totalAppliedFilters = appState.numberOfAppliedResourceFilters;

        return html`
            <div class="filter-buttons topbar-element">
                <button type="button"
                        ?disabled=${totalAppliedFilters < 1}
                        data-disabled=${totalAppliedFilters < 1 ? 'true' : 'false'}
                        data-total=${totalAppliedFilters}
                        class=${classMap({
                            reset: true,
                            secondary: true,
                            'buttonform-marker': true,
                            'filter-applied': totalAppliedFilters > 0,
                        })}
                        @click=${() => actionRegistry.execute(ActionIds.RESOURCE_FILTERS_CLEAR)}>
                    <div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="filter buttonform-marker secondary"
                        data-label="${translate('toolbar.filter')}"
                        @click=${() => actionRegistry.execute(ActionIds.SIDEBAR_TOGGLE_TODO)}>
                    <div class="hover-effect"></div>
                </button>
            </div>
        `;
    },
};
